/**
 * Branding configuration for Pretty Chitty / Really Chitty sites
 *
 * The brand is determined by the VITE_BRAND environment variable.
 * - "prettychitty" (default): Licensed games, server-authoritative
 * - "reallychitty": User-created games, client-trust mode
 */

export type BrandId = "prettychitty" | "reallychitty";

export interface BrandColors {
  backgroundDark: string;
  backgroundPaper: string;
  primary: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
  accent: string;
  footerBackground: string;
  textPrimary: string;
  textSecondary: string;
}

export interface BrandConfig {
  id: BrandId;
  name: string;
  tagline: string;
  description: string;
  warningTitle: string;
  warningMessage: string;
  infoCardTitle: string;
  infoCardText: string;
  featureCardTitle: string;
  featureCardText: string;
  colors: BrandColors;
  logoPath: string;
  faviconPath: string;
}

const prettyChittyConfig: BrandConfig = {
  id: "prettychitty",
  name: "Pretty Chitty",
  tagline: "Board games. No board required.",
  description:
    "Strategy board games, beautifully rendered in 3D. Play with friends async or live, on your phone or PC.",
  warningTitle: "Free to Play",
  warningMessage:
    "Pretty Chitty is free for personal, non-commercial use. Want to see your game here?",
  infoCardTitle: "What is Pretty Chitty?",
  infoCardText:
    "A board game engine that brings strategy games to life. Beautiful cards, tokens, and boards—all the chits you love, none of the setup.",
  featureCardTitle: "Play Your Way",
  featureCardText:
    "Jump in for a quick live game or play async over days. Works on desktop and mobile. Invite friends via Discord or share a link.",
  colors: {
    backgroundDark: "#2D0462",
    backgroundPaper: "#450693",
    primary: "#FF3F7F",
    primaryLight: "#FF6699",
    primaryDark: "#CC3366",
    secondary: "#8C00FF",
    secondaryLight: "#A333FF",
    secondaryDark: "#6B00C2",
    accent: "#FFC400",
    footerBackground: "#1a0338",
    textPrimary: "#ffffff",
    textSecondary: "rgba(255, 255, 255, 0.7)",
  },
  logoPath: "/assets/full_logo.png",
  faviconPath: "/favicon.svg",
};

const reallyChittyConfig: BrandConfig = {
  id: "reallychitty",
  name: "Really Chitty",
  tagline: "Your rules. Your bugs.",
  description:
    "Play weird, wonderful board games made by the community. Log in with Discord, pick a game, and play with friends.",
  warningTitle: "Community Games",
  warningMessage:
    "Games here are created and hosted by the community. They run in your browser, so buggy games may crash your tab. Play with people you trust!",
  infoCardTitle: "What is Really Chitty?",
  infoCardText:
    "An open platform where anyone can publish their own board games. You host the game code on your CDN, and Really Chitty handles matchmaking and game state.",
  featureCardTitle: "Publish Your Game",
  featureCardText:
    "Create a game with the Pretty Chitty SDK, host it anywhere, and submit it to Really Chitty. No approval process—just build and share.",
  colors: {
    backgroundDark: "#1A2540",
    backgroundPaper: "#26355D",
    primary: "#FFDB00",
    primaryLight: "#FFE540",
    primaryDark: "#CCB000",
    secondary: "#AF47D2",
    secondaryLight: "#C76DE3",
    secondaryDark: "#8A38A8",
    accent: "#FFDB00",
    footerBackground: "#141C33",
    textPrimary: "#FFFFFF",
    textSecondary: "rgba(255, 255, 255, 0.8)",
  },
  logoPath: "/assets/reallychitty_logo.png",
  faviconPath: "/reallychitty-favicon.svg",
};

function getBrandId(): BrandId {
  const brand = import.meta.env.VITE_BRAND as string | undefined;
  if (brand === "reallychitty") {
    return "reallychitty";
  }
  return "prettychitty";
}

export function getBrandConfig(): BrandConfig {
  const brandId = getBrandId();
  return brandId === "reallychitty" ? reallyChittyConfig : prettyChittyConfig;
}

export function isReallyChitty(): boolean {
  return getBrandId() === "reallychitty";
}

export function isPrettyChitty(): boolean {
  return getBrandId() === "prettychitty";
}

// Export the current brand config as a convenience
export const brand = getBrandConfig();
